import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LoggingService } from '@app-core';
import { BehaviorSubject } from 'rxjs';

interface FileError {
    title: string;
    messages: string[];
}

@Component({
    selector: 'app-upload-file',
    templateUrl: './upload-file.component.html',
    styleUrls: ['./upload-file.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule],
})
export class UploadFileComponent {
    @Output() filesChanged: EventEmitter<any[]> = new EventEmitter();
    @Input() maxFiles: number;
    @Input() maxFileMb: number = 2;
    @Input() allowedTypes: string[];
    maxFileSize: number;
    dropZone = document.getElementById('drop-zone');
    fileInput = document.getElementById('file-input');
    isLoading = new BehaviorSubject(false);
    isDisabled: boolean = false;
    files: any[] = [];
    fileIcon: string = '';
    fileErrors: FileError = {
        title: 'One or more files cannot be uploaded',
        messages: [],
    };

    constructor(private readonly elementRef: ElementRef) {}

    ngAfterViewInit() {
        this.maxFileSize = this.maxFileMb * 1024 * 1024;
        const dropZone = this.elementRef.nativeElement.querySelector('#drop-zone');

        if (dropZone) {
            // Add 'fileover' class when a file is dragged over the drop zone
            dropZone.addEventListener('dragover', (event: DragEvent) => {
                event.preventDefault(); // Prevent default to allow drop
                dropZone.classList.add('fileover');
            });

            // Remove 'fileover' class when the drag leaves the drop zone
            dropZone.addEventListener('dragleave', () => {
                dropZone.classList.remove('fileover');
            });

            // Remove 'fileover' class when a file is dropped
            dropZone.addEventListener('drop', (event: DragEvent) => {
                event.preventDefault(); // Prevent default behavior (e.g., opening the file)
                dropZone.classList.remove('fileover');

                // Handle the dropped files
                const files = event.dataTransfer?.files;
                if (files && files.length > 0) {
                    this.prepareFilesList(Array.from(files));
                    this.filesChanged.emit(this.files);
                }
            });
        }
    }

    onFileDropped(event: any) {
        this.prepareFilesList(event);
        this.filesChanged.emit(this.files);
    }

    fileBrowseHandler(files: any) {
        this.prepareFilesList(files);
        this.filesChanged.emit(this.files);
    }

    deleteFile(index: number) {
        this.files.splice(index, 1);
        if (this.files.length == 0) {
            this.fileErrors.messages = [];
        }
        this.isDisabled = false;
        this.filesChanged.emit(this.files);
    }

    clearFiles() {
        this.files = [];
        this.fileErrors.messages = [];
        this.isDisabled = false;
        this.filesChanged.emit(this.files);
    }

    /** Convert File list to normal array list */
    prepareFilesList(files: Array<any>) {
        this.fileErrors.messages = [];
        LoggingService.log(files);
        if (this.files.length > this.maxFiles - 1 || files.length > this.maxFiles) {
            this.fileErrors.messages.push('A maximum of ' + this.maxFiles + ' files can be uploaded.');
        } else {
            for (const file of files) {
                if (!this.allowedTypes.includes(file.type)) {
                    this.fileErrors.messages.push(`The file <strong>${file.name}</strong> is not of a type we can accept for upload. We can accept PDFs and most image formats.`);
                } else if (file.size > this.maxFileSize) {
                    this.fileErrors.messages.push(`The file <strong>${file.name}</strong> exceeds the maximum file size of 2mb.`);
                } else if (file.size == 0) {
                    this.fileErrors.messages.push(`The file <strong>${file.name}</strong> is empty or contains no content.`);
                } else {
                    file.progress = 0;
                    this.files.push(file);
                }
            }
        }
        if (this.files.length === this.maxFiles) {
            this.isDisabled = true;
        }
    }

    /* format bytes into different units */
    formatBytes(bytes: number, decimals: number) {
        if (bytes === 0) {
            return '0 Bytes';
        }
        const k = 1024;
        const dm = decimals <= 0 ? 0 : decimals || 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    /* Simulate the upload process */
    uploadFilesSimulator(index: number) {
        setTimeout(() => {
            if (index === this.files.length) {
                return;
            } else {
                const progressInterval = setInterval(() => {
                    if (this.files[index].progress === 100) {
                        clearInterval(progressInterval);
                        this.uploadFilesSimulator(index + 1);
                    } else {
                        this.files[index].progress += 5;
                    }
                }, 200);
            }
        }, 1000);
    }

    getFileIcon(fileType: string): string {
        switch (fileType) {
            case 'application/pdf':
                return 'fa-light fa-file-pdf';
            case 'application/msword':
                return 'fa-light fa-file-word   ';
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                return 'fa-light fa-file-word';
            case 'application/vnd.ms-excel':
                return 'fa-light fa-file-excel';
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                return 'fa-light fa-file-excel';
            case 'image/png':
                return 'fa-light fa-file-png';
            case 'image/jpeg':
                return 'fa-solid fa-file-jpg';
            case 'image/jpg':
                return 'fa-solid fa-file-jpg';
            case 'image/gif':
                return 'fa-light fa-file-image';
            case 'image/bmp':
                return 'fa-light fa-file-image';
            case 'image/tiff':
                return 'fa-light fa-file-image';
            default:
                return 'fa-light fa-file';
        }
    }
}
